import { useSearchParams } from "react-router-dom";
import { useMemo, useCallback } from "react";

interface UseTabStateOptions {
  /** URL query parameter name (default: "tab") */
  paramName?: string;
  /** Default tab index if no param is present */
  defaultTab?: number;
  /** Mapping from tab index to URL value */
  indexToValue: (index: number) => string | null;
  /** Mapping from URL value to tab index */
  valueToIndex: (value: string) => number | null;
}

/**
 * Custom hook to manage tab state via URL query parameters
 * Provides a single source of truth for tab selection that persists across page reloads
 */
export function useTabState({
  paramName = "tab",
  defaultTab = 0,
  indexToValue,
  valueToIndex,
}: UseTabStateOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current active tab from URL
  const activeTab = useMemo(() => {
    const paramValue = searchParams.get(paramName);
    if (paramValue) {
      const index = valueToIndex(paramValue);
      if (index !== null) {
        return index;
      }
    }
    return defaultTab;
  }, [searchParams, paramName, valueToIndex, defaultTab]);

  // Update URL when tab changes
  const setActiveTab = useCallback(
    (index: number) => {
      const value = indexToValue(index);
      if (value !== null) {
        setSearchParams({ [paramName]: value }, { replace: true });
      }
    },
    [setSearchParams, paramName, indexToValue]
  );

  return [activeTab, setActiveTab] as const;
}

